/**
 * KAN-877 — the boot-time cover for a Stripe return. Plain DOM (no React, tiny, lives in the entry chunk): armed from
 * index.js BEFORE the first render, so the boot splash fades onto an opaque canvas cover and never onto the Plan &
 * billing page / app behind it. The React `ReturnCover` (same color) takes over in the commit where the Confirming
 * card mounts.
 *
 * Armed ONLY when THIS device just started a return: `?checkout=done` on the Plan & billing path AND a fresh
 * checkout / portal marker in localStorage (only the owner/admin who left for Stripe from here writes one). Never blocks
 * input (pointer-events none). Every exit removes it: the card's cover takes over, `?checkout=cancelled`, "Let's go",
 * an auth screen mounting (AuthScreenShell / MFA gate), the route leaving Plan & billing (a short poll), and a 6s cap.
 */
import { foundation } from '../../ui/tokens';
import { peekCheckoutDone } from './peekCheckoutDone';
import { SUBSCRIBE_PENDING_PREFIX, SUBSCRIBE_PENDING_TTL_MS } from './subscribeCheckoutMarker';
import { PORTAL_RETURN_PREFIX, PORTAL_RETURN_TTL_MS } from './portalReturnMarker';

export const BOOT_RETURN_COVER_ID = 'cvx-boot-return-cover';
export const BOOT_RETURN_COVER_CAP_MS = 6000;
const ROUTE_POLL_MS = 150;
const PLAN_PATH = /^\/app\/company\/plan(\/|$)/;
// Above every page layer incl. the AI floating button, just under the splash (100000). Never coexists with the card:
// the React ReturnCover removes it in the card's first commit.
const Z = 99999;

let capTimer = null;
let pollTimer = null;

export function removeBootReturnCover() {
  if (capTimer) { clearTimeout(capTimer); capTimer = null; }
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  if (typeof document === 'undefined') return;
  const el = document.getElementById(BOOT_RETURN_COVER_ID);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/** A fresh checkout or portal marker for ANY user/tenant on this device (same keys + freshness as the app; the user is not known yet at boot). */
export function hasFreshReturnMarker(nowMs = Date.now(), storage) {
  try {
    const s = storage || window.localStorage;
    for (let i = 0; i < s.length; i += 1) {
      const key = s.key(i) || '';
      const isCheckout = key.startsWith(SUBSCRIBE_PENDING_PREFIX);
      if (!isCheckout && !key.startsWith(PORTAL_RETURN_PREFIX)) continue;
      const m = JSON.parse(s.getItem(key) || 'null');
      const ttl = isCheckout ? SUBSCRIBE_PENDING_TTL_MS : PORTAL_RETURN_TTL_MS;
      if (m && Number.isFinite(m.startedAt) && nowMs - m.startedAt < ttl) return true;
    }
  } catch { /* blocked storage: no marker, no cover */ }
  return false;
}

/** Mount the cover iff this device just started a Stripe return. Idempotent. Returns whether a cover is up. */
export function armBootReturnCover() {
  if (typeof document === 'undefined' || !peekCheckoutDone()) return false;
  if (!PLAN_PATH.test(window.location.pathname) || !hasFreshReturnMarker()) return false;
  if (document.getElementById(BOOT_RETURN_COVER_ID)) return true;
  const el = document.createElement('div');
  el.id = BOOT_RETURN_COVER_ID;
  el.setAttribute('aria-hidden', 'true');
  el.setAttribute('data-testid', 'boot-return-cover');
  el.style.cssText = `position:fixed;top:0;left:0;right:0;height:max(100dvh,100lvh);z-index:${Z};pointer-events:none;background:${foundation.color.neutral.bg};`;
  document.body.appendChild(el);
  capTimer = setTimeout(removeBootReturnCover, BOOT_RETURN_COVER_CAP_MS);
  // The route moved off Plan & billing (a redirect to another home / the login route): yield.
  pollTimer = setInterval(() => { if (!PLAN_PATH.test(window.location.pathname)) removeBootReturnCover(); }, ROUTE_POLL_MS);
  return true;
}
